import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, fromEvent, map } from 'rxjs';
import { UnifiedAnalyticsService } from './unified-analytics.service';

/**
 * Simplified Analytics Service - Connects user auth with analytics
 * All tracking is automatic, no manual configuration needed
 * UnifiedAnalyticsService handles both Mixpanel and Google Analytics
 */
@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private readonly unifiedAnalyticsService = inject(UnifiedAnalyticsService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.setupUserTracking();
    }

    /**
     * Setup automatic user tracking
     */
    private setupUserTracking(): void {
        // Track user login/logout via local storage token changes
        fromEvent(window, 'storage')
            .pipe(
                map(() => ({
                    token: localStorage.getItem('token'),
                    userId: localStorage.getItem('userId'),
                })),
                distinctUntilChanged((prev, curr) => prev.token === curr.token && prev.userId === curr.userId),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({ token, userId }: { token: string | null; userId: string | null }) => {
                if (token && userId) {
                    // User logged in - MixpanelService will auto-detect from localStorage
                    this.unifiedAnalyticsService.track('User Login Detected', {
                        detection_method: 'localStorage_change',
                        has_token: !!token,
                        has_user_id: !!userId,
                    });
                } else {
                    // User logged out
                    this.unifiedAnalyticsService.userSignedOut();
                }
            });
    }

    /**
     * Track custom events (UnifiedAnalyticsService handles both platforms)
     */
    track(eventName: string, properties?: Record<string, unknown>): void {
        this.unifiedAnalyticsService.track(eventName, properties);
    }

    /**
     * Track user actions (button clicks, form submissions, etc.)
     */
    trackUserAction(action: string, category?: string, label?: string, value?: number): void {
        this.unifiedAnalyticsService.track('user_action', {
            action,
            category: category || 'general',
            label,
            value,
        });
    }

    /**
     * Track feature usage across the application
     */
    trackFeatureUsage(featureName: string, properties?: Record<string, unknown>): void {
        this.unifiedAnalyticsService.track('feature_usage', {
            feature_name: featureName,
            ...properties,
        });
    }

    /**
     * Track errors for monitoring and debugging
     */
    trackError(error: string, category?: string): void {
        this.unifiedAnalyticsService.track('error', {
            error_message: error,
            error_category: category || 'general',
        });
    }

    /**
     * Get tracking status for debugging (both platforms)
     */
    getStatus(): unknown {
        return this.unifiedAnalyticsService.getStatus();
    }

    /**
     * Check if analytics is initialized
     */
    get isInitialized(): boolean {
        return this.unifiedAnalyticsService.initialized();
    }
}
